import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaDownload } from 'react-icons/fa';
import * as constants from '../../constants/CONSTANT';

const TELECMI_AUDIO_PLAY_EVENT = 'telecmi-audio-play';

function getAuthToken() {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('watconnect_token') ||
    ''
  );
}

/** Fetch recording via authenticated proxy and trigger browser download (no secrets in URL). */
export async function downloadTelecmiRecordingFile(filename) {
  const name = filename == null ? '' : String(filename).trim();
  if (!name) {
    return false;
  }
  const token = getAuthToken();
  if (!token) {
    return false;
  }
  const url = `${constants.API_BASE_URL}/api/telecmi/recording?file=${encodeURIComponent(
    name
  )}`;
  try {
    const res = await fetch(url, { headers: { Authorization: token } });
    if (!res.ok) {
      return false;
    }
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
    return true;
  } catch {
    return false;
  }
}

/**
 * Plays TeleCMI recordings through the authenticated proxy.
 * Plain <audio src="..."> does not send Authorization, so we fetch as a blob first.
 */
export default function TelecmiRecordingAudio({
  filename,
  audioStyle,
  className,
  showDownload = true,
  downloadVariant = 'button',
}) {
  const [audioSrc, setAudioSrc] = useState(null);
  const [status, setStatus] = useState('loading');
  const blobUrlRef = useRef(null);
  const blobRef = useRef(null);
  const audioElRef = useRef(null);
  const instanceIdRef = useRef(
    `telecmi-audio-${Math.random().toString(36).slice(2)}-${Date.now()}`
  );

  const trimmedName = useMemo(
    () => (filename == null ? '' : String(filename).trim()),
    [filename]
  );
  const recordingUrl = useMemo(() => {
    if (!trimmedName) return null;
    return `${constants.API_BASE_URL}/api/telecmi/recording?file=${encodeURIComponent(
      trimmedName
    )}`;
  }, [trimmedName]);

  useEffect(() => {
    let cancelled = false;
    if (!trimmedName) {
      setStatus('empty');
      return undefined;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    blobRef.current = null;
    setAudioSrc(null);
    setStatus('loading');

    const token = getAuthToken();
    if (!token) {
      setStatus('auth');
      return undefined;
    }
    if (!recordingUrl) {
      setStatus('error');
      return undefined;
    }

    (async () => {
      try {
        const res = await fetch(recordingUrl, {
          headers: { Authorization: token },
        });
        if (!res.ok) {
          setStatus(res.status === 401 ? 'auth' : 'error');
          return;
        }
        const blob = await res.blob();
        if (cancelled) {
          return;
        }
        blobRef.current = blob;
        const objectUrl = URL.createObjectURL(blob);
        blobUrlRef.current = objectUrl;
        setAudioSrc(objectUrl);
        setStatus('ready');
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      blobRef.current = null;
    };
  }, [trimmedName, recordingUrl]);

  // Ensure only one audio plays at a time across the app
  useEffect(() => {
    const onOtherPlay = (e) => {
      try {
        const detail = e?.detail || {};
        if (detail.instanceId === instanceIdRef.current) {
          return;
        }
        const el = audioElRef.current;
        if (el && !el.paused) {
          el.pause();
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener(TELECMI_AUDIO_PLAY_EVENT, onOtherPlay);
    return () => {
      window.removeEventListener(TELECMI_AUDIO_PLAY_EVENT, onOtherPlay);
    };
  }, []);

  if (status === 'empty') {
    return null;
  }
  if (status === 'auth') {
    return (
      <span style={{ fontSize: 12, color: '#888' }}>Sign in to play recording</span>
    );
  }
  if (status === 'loading' || !audioSrc) {
    return <span style={{ fontSize: 12, color: '#888' }}>Loading…</span>;
  }
  if (status === 'error') {
    return <span style={{ fontSize: 12, color: '#888' }}>Could not load recording</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <audio
        ref={audioElRef}
        controls
        preload="metadata"
        className={className}
        style={audioStyle}
        src={audioSrc}
        onPlay={() => {
          try {
            window.dispatchEvent(
              new CustomEvent(TELECMI_AUDIO_PLAY_EVENT, {
                detail: { instanceId: instanceIdRef.current },
              })
            );
          } catch {
            // ignore
          }
        }}
      />
      {showDownload && (
        <button
          type="button"
          title="Download recording"
          aria-label="Download recording"
          onClick={() => {
            try {
              const b = blobRef.current;
              if (b && trimmedName) {
                const href = URL.createObjectURL(b);
                const a = document.createElement('a');
                a.href = href;
                a.download = trimmedName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(href);
              } else {
                downloadTelecmiRecordingFile(trimmedName);
              }
            } catch {
              // ignore
            }
          }}
          style={
            downloadVariant === 'icon'
              ? {
                  border: 'none',
                  background: 'transparent',
                  padding: 6,
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: '#495057',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              : {
                  border: '1px solid #ddd',
                  background: '#fff',
                  padding: '6px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                }
          }
        >
          {downloadVariant === 'icon' ? <FaDownload size={16} /> : 'Download'}
        </button>
      )}
    </div>
  );
}
